import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import emberSwal from 'ui-kit/utils/ember-swal';
import Button from '../button.gts';

interface Signature {
  Element: HTMLButtonElement;
  Args: {
    value: string | null;
    selectedValue: string;
    isReadOnly?: boolean;
    keepSelected: boolean;
    onChange: (value: string | null) => void;
    onSelectedUpdate: (value: string | null) => void;
    xs?: boolean;
    selectedColor?: string;
    shouldConfirmSelection?: boolean;
    confirmationTitle?: string;
    confirmationText?: string;
    normalCase?: boolean;
  };
  Blocks: { default: [] };
}

export default class SelectableButtonItem extends Component<Signature> {
  get value(): string | null {
    return this.args.value;
  }

  get isSelected() {
    return this.value != null && this.value === this.args.selectedValue;
  }

  update = async () => {
    if (!this.args.isReadOnly) {
      if (this.args.shouldConfirmSelection) {
        const result = await emberSwal.fire({
          title: this.args.confirmationTitle,
          text: this.args.confirmationText,
          icon: 'warning',
          cancelButtonText: 'Annuler',
          showCancelButton: true,
          confirmButtonText: 'Confirmer',
          confirmButtonColor: '#d33'
        });

        if (!result.isConfirmed) {
          return;
        }
      }
      if (!this.args.keepSelected) {
        this.args.onChange(this.isSelected ? null : this.value);
      } else {
        if (!this.isSelected) {
          this.args.onChange(this.value);
        }
      }
      this.args.onSelectedUpdate?.(this.value);
    }
  };

  get disabledStyle() {
    return this.args.isReadOnly ? 'disabled' : '';
  }

  get sizeStyle() {
    return this.args.xs ? 'p-3' : '';
  }

  get caseStyle() {
    return this.args.normalCase ? '!normal-case' : '';
  }

  get selectedStyle() {
    if (!this.isSelected) return '';

    // For test purposes only. This is the legacy ember-css-modules string that was used in tests
    const testStyle = 'is-selected-btn';

    if (this.args.selectedColor == 'grey')
      return `${testStyle} !bg-[#808080] !text-black`;
    if (this.args.selectedColor == 'grey-light')
      return `${testStyle} !bg-[#C0C1C2] !text-black`;
    if (this.args.selectedColor == 'teal')
      return `${testStyle} !bg-teal !text-white`;

    return `${testStyle} !bg-pink !text-white`;
  }

  get styles() {
    return `${this.disabledStyle} ${this.sizeStyle} ${this.selectedStyle} ${this.caseStyle}`;
  }

  <template>
    <Button
      ...attributes
      class={{this.styles}}
      type='button'
      data-test-selectable-btn={{@value}}
      data-test-selected={{if this.isSelected 'true' 'false'}}
      {{on 'click' this.update}}
    >
      {{yield}}
    </Button>
  </template>
}
